import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import incrementUnique from 'data/lib/increment-unique'
import { diff_obj } from 'data/lib/diff'
import notificationService from "data/notification"

import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type TenantGroup = {
  propertyid: number
  tenantgroupid: number
}
// private module logic //

const id: string = "tenantGroup"
const initialState: ApiResult<TenantGroup[]> = { id, state: "init", value: [] }
const tenantGroupStream = new BehaviorSubject<ApiResult<TenantGroup[]>>(initialState)
const tenantGroupEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let tenantGroupMap = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  tenantGroupStream.next({ id, state: 'ok', value: [] })
  tenantGroupMap = {}
}

function get(tenantgroupid: number): TenantGroup {
  return tenantGroupMap[tenantgroupid]
}

function refresh(): Promise<any> {
  const currentValue = tenantGroupStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchTenantGroups(propertyid)
}

function copy(propertyid:number, tenantgroupid:number, code:string) {
  const currentTenantGroupVal = tenantGroupStream.getValue().value;
  tenantGroupStream.next({ id, state: 'busy', value: currentTenantGroupVal });

  const payload = {table: 'tenantgroup', recordid: tenantgroupid, _synchronous: true, code: incrementUnique(code, "code", currentTenantGroupVal)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((tenantGroup: TenantGroup) => {
      const newRecord = tenantGroup[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.tenantgroupid),
        redofunc: () => copy(propertyid, tenantgroupid, code)
      });
      tenantGroupMap[newRecord.tenantgroupid] = newRecord
      // merge results unique by tenantgroupid
      const currentTenantGroupVal = tenantGroupStream.getValue().value
      tenantGroupStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentTenantGroupVal, 'tenantgroupid').sort((a, b) => alphanum(a.code, b.code))
      })
      tenantGroupEventStream.next({ type: 'TENANTGROUP_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      tenantGroupStream.next({ id, state: 'error', error: err, value: currentTenantGroupVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchTenantGroups(propertyid)
      return err
    })
}

function copies(propertyid: number, tenantgroupid: number, count: number) {
  const currentTenantGroupVal = tenantGroupStream.getValue().value;
  tenantGroupStream.next({ id, state: 'busy', value: currentTenantGroupVal });

  const payload = {table: 'tenantgroup', recordid: tenantgroupid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((tenantgroups: TenantGroup[]) => {
      push_undo({
        undofunc: () => _.forEach(newRecord => deleteOne(propertyid, newRecord.tenantgroupid)),
        redofunc: () => copies(propertyid, tenantgroupid, count)
      });
      _.forEach(tenantgroups, newRecord => tenantGroupMap[newRecord.tenantgroupid] = newRecord)
      // merge results unique by tenantgroupid
      const currentTenantGroupVal = tenantGroupStream.getValue().value
      tenantGroupStream.next({
        id,
        state: 'ok',
        value: _.unionBy(tenantgroups, currentTenantGroupVal, 'tenantgroupid').sort((a, b) => alphanum(a.code, b.code))
      })
      tenantGroupEventStream.next({ type: 'TENANTGROUP_COPIED', payload: tenantgroups })
      return tenantgroups
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      tenantGroupStream.next({ id, state: 'error', error: err, value: currentTenantGroupVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchTenantGroups(propertyid)
      return err
    })
}

function create(propertyid: number): Promise<TenantGroup | { message: string }> {
  const currentValue = tenantGroupStream.getValue().value
  tenantGroupStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    tenantgroupdetail: {}
  }

  return Client.post(`tenantgroup`, payload).then((tenantGroups: TenantGroup) => {
    push_undo({
      undofunc: () => deleteOne(propertyid, tenantGroups.tenantgroupid),
      redofunc: () => create(propertyid)
    })
    tenantGroupMap[tenantGroups.tenantgroupid] = tenantGroups
    // merge results unique by tenantgroupid
    const currentValue = tenantGroupStream.getValue().value
    tenantGroupStream.next({
      id,
      state: 'ok',
      value: _.unionBy([tenantGroups], currentValue, 'tenantgroupid')
    })
    tenantGroupEventStream.next({ type: 'TENANTGROUP_CREATED', payload: tenantGroups })
    return tenantGroups
  })
  .catch((err: { message: string }) => {
    // on error overwrite optimistic update
    tenantGroupStream.next({ id, state: 'error', error: err, value: currentValue })
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    fetchTenantGroups(propertyid)
    return err
  })
}

async function deleteOne(
  propertyid: number,
  tenantgroupid: number
): Promise<boolean | { message: string }> {
  const currentValue = tenantGroupStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, tenantgroupid)
  const refreshFn = () => fetchTenantGroups(propertyid)
  if (!(await handleReferences('tenantgroup', tenantgroupid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted tenantgroupid
  const value = _(currentValue)
    .filter((p) => p.tenantgroupid !== tenantgroupid)
    .value()
  value.forEach((p) => (tenantGroupMap[p.tenantgroupid] = p))

  tenantGroupStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(tenantgroupid).then(() =>
  Client.del(`tenantgroup?tenantgroupid=eq.${tenantgroupid}`)
    .then((tenantGroups: TenantGroup) => {
      delete tenantGroupMap[tenantGroups.tenantgroupid]
      push_undo({
        undofunc: () => {
          Client.post('tenantGroups', tenantGroups)
          tenantGroupMap[tenantGroups.tenantgroupid] = tenantGroups
          tenantGroupStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, tenantgroupid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      tenantGroupStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchTenantGroups(propertyid)
      return err
    })
  )
}

function fetchTenantGroups(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<TenantGroup[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  tenantGroupStream.next({ id, state: 'busy', value: tenantGroupStream.getValue().value })

  let url = `tenantgroup?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `tenantgroup?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: TenantGroup[]) => {
    _.forEach(records, (tenantGroups) => (tenantGroupMap[tenantGroups.tenantgroupid] = tenantGroups))
    // merge results unique by tenantgroupid
    tenantGroupStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function patch(tenantGroups: TenantGroup): Promise<TenantGroup | { message: string }> {
  const currentValue = tenantGroupStream.getValue().value
  const tenantgroupid: number = tenantGroups.tenantgroupid
  const currentTenantGroups = _.find(currentValue, { tenantgroupid })
  const changeset = diff_obj(tenantGroups, currentTenantGroups)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentTenantGroups),
    redofunc: () => patch(tenantGroups)
  })

  let value = _.cloneDeep(currentValue)
  value.splice(_.findIndex(currentValue, a => a.tenantgroupid === tenantgroupid), 1, tenantGroups)

  // optimistic update
  tenantGroupStream.next({
    id,
    state: 'ok',
    value
  })

  return _patches_.watch(tenantgroupid, Client.patch(`tenantgroup?tenantgroupid=eq.${tenantgroupid}`, changeset)
    .then((mf: TenantGroup[]) => {
      _.forEach(mf, (tenantGroups) => (tenantGroupMap[tenantGroups.tenantgroupid] = tenantGroups))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      tenantGroupStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentTenantGroups, 'propertyid')
      if (propertyid) fetchTenantGroups(propertyid)
      return err
    }))
}

// public module interface //

const tenantGroupService = {
  stream: () => tenantGroupStream.asObservable(),
  value: () => tenantGroupStream.getValue().value,
  eventStream: () => tenantGroupEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  get,
  fetchTenantGroups,
  patch,
  refresh
}

export default tenantGroupService
